// web/src/theme/branding.ts
// Whitelist parser + CSS emitter for tenant branding. Mirrors the backend
// api/services/branding.py contract. Defense in depth: the widget re-validates
// whatever the API returns before any value touches CSS.
//
// The allowlist DATA (color fields, font keys/slots, caps, regex sources, the
// display->title alias) is single-sourced from the repo-root branding-contract.json
// (ADR-0005). The Python sanitizer reads the same file; parity tests on both
// sides fail CI if either validator's literals drift from the contract. Only the
// data is shared — both validators keep their own logic (defense in depth).
import contract from "../../../branding-contract.json";

export type ColorField =
  | "accent" | "accentStrong" | "onAccent" | "ink"
  | "surface" | "muted" | "subtle" | "border" | "background";

export type FontKey =
  | "inter" | "space-grotesk"
  | "montserrat" | "dm-sans" | "work-sans" | "lora" | "bebas-neue"
  | "arpona" | "gill-sans";

export type FontSlot = "title" | "body" | "cta";

export type CtaTransform = "none" | "uppercase";

export interface Branding {
  colors?: Partial<Record<ColorField, string>>;
  radius?: string;
  borderWidth?: string;
  logo?: { src: string; alt: string };
  fonts?: Partial<Record<FontSlot, FontKey>>;
  ctaTransform?: CtaTransform;
  ctaTracking?: string;
}

// Color field -> CSS custom property (the names tokens.css exposes).
const COLOR_VARS: Record<ColorField, string> = {
  accent: "--color-accent",
  accentStrong: "--color-accent-strong",
  onAccent: "--color-on-accent",
  ink: "--color-ink",
  surface: "--color-surface",
  muted: "--color-muted",
  subtle: "--color-subtle",
  border: "--color-border",
  background: "--color-background",
};

// Font slot -> CSS custom property.
const FONT_VARS: Record<FontSlot, string> = {
  title: "--font-title",
  body: "--font-body",
  cta: "--font-cta",
};

// Font key -> family stack (must match the families in fonts.ts / backend FONT_KEYS).
// The KEY SET is pinned by branding-contract.json (parity-tested); the family
// stack strings are web-only rendering detail.
export const FONT_ALLOWLIST: Record<FontKey, string> = {
  inter: '"Inter Variable", system-ui, sans-serif',
  "space-grotesk": '"Space Grotesk Variable", "Inter Variable", system-ui, sans-serif',
  montserrat: '"Montserrat Variable", system-ui, sans-serif',
  "dm-sans": '"DM Sans Variable", system-ui, sans-serif',
  "work-sans": '"Work Sans Variable", system-ui, sans-serif',
  lora: '"Lora Variable", Georgia, serif',
  "bebas-neue": '"Bebas Neue", "Helvetica Neue", Arial, sans-serif',
  arpona: '"Arpona", "Times New Roman", Georgia, serif',
  "gill-sans": '"Gill Sans", "Gill Sans MT", Calibri, "Trebuchet MS", sans-serif',
};

// Caps + alias + regex sources, all single-sourced from the contract.
const RADIUS_CAP_PX = contract.radiusCaps.px;
const RADIUS_CAP_REM = contract.radiusCaps.rem;
const BORDER_CAP_PX = contract.borderWidthCaps.px;
const BORDER_CAP_REM = contract.borderWidthCaps.rem;
const LOGO_MAX_SRC = contract.logo.maxSrcLength;
const LOGO_MAX_ALT = contract.logo.maxAltLength;
const LOGO_SRC_PREFIX = contract.logo.srcPrefix;
const [FONT_ALIAS_FROM, FONT_ALIAS_TO] = Object.entries(contract.fontSlotAlias)[0];
const CTA_TRANSFORM_VALUES = contract.ctaTransformValues as readonly string[];
const CTA_TRACKING_CAP_EM = contract.ctaTrackingCapEm;

const HEX = new RegExp(contract.regex.hex);
const LEN = new RegExp(contract.regex.length);
const EM = new RegExp(contract.regex.em);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function validHex(v: unknown): v is string {
  return typeof v === "string" && HEX.test(v);
}

function validRadius(v: unknown): v is string {
  if (typeof v !== "string") return false;
  const m = LEN.exec(v);
  if (!m) return false;
  const num = Number.parseFloat(v);
  return m[1] === "px" ? num <= RADIUS_CAP_PX : num <= RADIUS_CAP_REM;
}

function validBorderWidth(v: unknown): v is string {
  if (v === "0") return true;
  if (typeof v !== "string") return false;
  const m = LEN.exec(v);
  if (!m) return false;
  const num = Number.parseFloat(v);
  return m[1] === "px" ? num <= BORDER_CAP_PX : num <= BORDER_CAP_REM;
}

function validCtaTransform(v: unknown): v is CtaTransform {
  return typeof v === "string" && CTA_TRANSFORM_VALUES.includes(v);
}

function validCtaTracking(v: unknown): v is string {
  if (typeof v !== "string" || !EM.test(v)) return false;
  return Number.parseFloat(v) <= CTA_TRACKING_CAP_EM;
}

function parseColors(raw: unknown): Branding["colors"] | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Partial<Record<ColorField, string>> = {};
  for (const key of Object.keys(COLOR_VARS) as ColorField[]) {
    if (validHex(raw[key])) out[key] = raw[key] as string;
  }
  return Object.keys(out).length ? out : undefined;
}

function parseLogo(raw: unknown): Branding["logo"] | undefined {
  if (!isRecord(raw)) return undefined;
  const src = raw.src;
  if (typeof src !== "string" || src.length > LOGO_MAX_SRC || !src.startsWith(LOGO_SRC_PREFIX)) {
    return undefined;
  }
  const alt = typeof raw.alt === "string" ? raw.alt.trim().slice(0, LOGO_MAX_ALT) : "";
  return { src, alt };
}

function parseFonts(raw: unknown): Branding["fonts"] | undefined {
  if (!isRecord(raw)) return undefined;
  const src: Record<string, unknown> = { ...raw };
  if (FONT_ALIAS_FROM in src && !(FONT_ALIAS_TO in src)) src[FONT_ALIAS_TO] = src[FONT_ALIAS_FROM]; // read-path alias
  const out: Partial<Record<FontSlot, FontKey>> = {};
  for (const slot of Object.keys(FONT_VARS) as FontSlot[]) {
    const v = src[slot];
    if (typeof v === "string" && v in FONT_ALLOWLIST) out[slot] = v as FontKey;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Whitelist-validate-drop. Never throws. */
export function parseBranding(raw: unknown): Branding {
  if (!isRecord(raw)) return {};
  const b: Branding = {};
  const colors = parseColors(raw.colors);
  if (colors) b.colors = colors;
  if (validRadius(raw.radius)) b.radius = raw.radius as string;
  if (validBorderWidth(raw.borderWidth)) b.borderWidth = raw.borderWidth as string;
  const logo = parseLogo(raw.logo);
  if (logo) b.logo = logo;
  const fonts = parseFonts(raw.fonts);
  if (fonts) b.fonts = fonts;
  if (validCtaTransform(raw.ctaTransform)) b.ctaTransform = raw.ctaTransform;
  if (validCtaTracking(raw.ctaTracking)) b.ctaTracking = raw.ctaTracking as string;
  return b;
}

/** Emit a `:host { … }` override of only the provided, valid variables. Returns
 * "" when there is nothing to set. Logo is NOT emitted (it is an <img>). */
export function brandingToCss(b: Branding): string {
  const decls: string[] = [];
  if (b.colors) {
    for (const key of Object.keys(COLOR_VARS) as ColorField[]) {
      const v = b.colors[key];
      if (v) decls.push(`${COLOR_VARS[key]}: ${v};`);
    }
  }
  if (b.radius) decls.push(`--radius-card: ${b.radius};`);
  if (b.borderWidth) decls.push(`--border-width: ${b.borderWidth};`);
  if (b.fonts) {
    for (const slot of Object.keys(FONT_VARS) as FontSlot[]) {
      const key = b.fonts[slot];
      if (key) decls.push(`${FONT_VARS[slot]}: ${FONT_ALLOWLIST[key]};`);
    }
  }
  if (b.ctaTransform) decls.push(`--cta-transform: ${b.ctaTransform};`);
  if (b.ctaTracking) decls.push(`--cta-tracking: ${b.ctaTracking};`);
  return decls.length ? `:host { ${decls.join(" ")} }` : "";
}
